import React, { useMemo } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { isMobile } from 'react-device-detect';
import { useTranslation } from 'react-i18next';
import { ICasinoGameSession } from '../../Casino/Interface/ICasinoGameSession';
import { CasinoConfig } from '../../Casino/CasinoConfig';

interface CasinoGameViewProps {
  sessions: ICasinoGameSession[];
  activeSessionId: string | null;
  visible: boolean;
  onBackToCasino: () => void;
}

const CasinoGameView: React.FC<CasinoGameViewProps> = ({
  sessions,
  activeSessionId,
  visible,
  onBackToCasino,
}) => {
  const { t } = useTranslation('Casino');
  // When detached mode is enabled, all sessions open in separate windows
  const isDetachedMode = CasinoConfig.launchInDetachedWindow;

  const hasActiveSession = useMemo(() => {
    if (!activeSessionId || isDetachedMode) return false;
    return sessions.some((session) => session.sessionId === activeSessionId);
  }, [sessions, activeSessionId, isDetachedMode]);

  // Check if active session exists (for detached mode message)
  const activeSessionExists = useMemo(() => {
    if (!activeSessionId) return false;
    return sessions.some((session) => session.sessionId === activeSessionId);
  }, [sessions, activeSessionId]);

  return (
    <Box
      sx={{
        display: visible ? 'flex' : 'none',
        position: 'relative',
        height: isMobile ? 'calc(100dvh - 63px)' : 'calc(100dvh - 140px)',
        width: '100%',
        boxSizing: 'border-box',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#121214',
      }}
    >
      {/* Only render iframes when not in detached mode */}
      {!isDetachedMode && (
        <Box sx={{ height: '100%', width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {sessions.map((session) => {
            const isActive = visible && session.sessionId === activeSessionId;
            return (
              <Box
                key={session.sessionId}
                sx={{
                  display: isActive ? 'flex' : 'none',
                  height: '100%',
                  width: '100%',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <iframe
                  src={session.url}
                  title={session.title}
                  style={{ width: '100%', height: '100%', border: 'none' }}
                  allow="autoplay; fullscreen; clipboard-write"
                  allowFullScreen
                />
              </Box>
            );
          })}
        </Box>
      )}

      {/* Show message when in detached mode and session exists */}
      {visible && isDetachedMode && activeSessionExists && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            height: '100%',
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2,
            fontFamily: 'Inter, serif',
          }}
        >
          <Typography variant="h6" style={{ fontFamily: 'Inter, serif' }}>
            {t('gameView.openInSeparateWindow')}
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={onBackToCasino}
            style={{ fontWeight: 'bold', backgroundColor: '#F52740', color: 'white' }}
          >
            {t('gameView.backToCasino')}
          </Button>
        </Box>
      )}

      {visible && !hasActiveSession && !(isDetachedMode && activeSessionExists) && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            height: '100%',
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2,
            fontFamily: 'Inter, serif',
          }}
        >
          <Typography variant="h6" style={{ fontFamily: 'Inter, serif' }}>
            {t('gameView.noGameSelected')}
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={onBackToCasino}
            style={{ fontWeight: 'bold', backgroundColor: '#F52740', color: 'white' }}
          >
            {t('gameView.backToCasino')}
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default CasinoGameView;
